import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';

export interface CaptionListing {
  title: string;
  description: string;
  price?: number | null;
  path: string;
}

@Injectable({ providedIn: 'root' })
export class ListingShareService {
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  async facebook(shareUrl: string, listing: CaptionListing): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) return '';
    // Start copying and open synchronously within the click's user activation.
    // Awaiting the clipboard first can cause browsers to block the share window.
    const copied = this.copy(this.caption(listing)).then(() => true, () => false);
    let openFailed = false;
    try {
      this.document.defaultView?.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        '_blank', 'noopener,noreferrer');
    } catch { openFailed = true; }
    if (!(await copied)) {
      return 'Caption could not be copied. Allow clipboard access and click Facebook again, then paste into your post.';
    }
    return openFailed
      ? 'Caption copied. Please allow pop-ups and click Facebook again, then paste your caption.'
      : 'Caption copied. Paste it into your Facebook post, then click Share. If Facebook did not open, allow pop-ups and try again.';
  }

  caption(listing: CaptionListing): string {
    // Decode entities after removing markup; never insert listing HTML into the live document.
    const text = listing.description.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
      .replace(/<[^>]*>/g, ' ');
    const decoder = this.document.createElement('textarea');
    decoder.innerHTML = text;
    const description = decoder.value.replace(/\s+/g, ' ').trim();
    const excerpt = description.length > 1200 ? description.slice(0, 1197).trimEnd() + '...' : description;
    const price = typeof listing.price === 'number' && Number.isFinite(listing.price)
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(listing.price) : '';
    return [listing.title.trim(), price, excerpt, new URL(listing.path, environment.publicUrl).toString()]
      .filter(Boolean).join('\n\n');
  }

  async copy(text: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      if (this.document.defaultView?.navigator.clipboard) {
        await this.document.defaultView.navigator.clipboard.writeText(text);
        return;
      }
    } catch { /* Fall back for browser clipboard restrictions. */ }
    const input = this.document.createElement('textarea');
    input.value = text;
    input.setAttribute('aria-label', 'Text to copy');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    this.document.body.appendChild(input);
    const focus = this.document.activeElement as HTMLElement | null;
    try {
      input.select();
      if (!this.document.execCommand('copy')) throw new Error('Clipboard unavailable');
    } finally { input.remove(); focus?.focus(); }
  }

}
